import React, { ReactNode } from "react";
import { connect } from "react-redux";
import Accessories from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/Accessories";
import BestBankRate from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/BestBankRate";
import DownPayment from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/DownPayment";
import Fees from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/Fees";
import Incentives from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/Incentives";
import Msrp from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/Msrp";
import PaymentType from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/PaymentType";
import Price from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/Price";
import Taxes from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/Taxes";
import Term from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/Term";
import TradeInEstimate from "src/components/shared/Sidebar/SidebarTable/SidebarTableRows/TradeInEstimate";
import { ApplicationStore } from "src/store";
import { getAccessoriesTotal, isAccessoriesEnabled } from "src/store/selectors/accessories";
import { canShowMsrp, canShowStrikeThroughMsrp } from "src/store/selectors/account";
import {
    getDealerOfferDownPaymentByVin,
    getDealerOfferInterestRateByVin,
    getDealerOfferLenderByVin,
    getDealerOfferPriceByVin,
    getDealerOfferTermByVin,
    getDealerOfferTotalAccessoriesByVin,
    getDealerOfferTotalFeesByVin,
    getDealerOfferTotalIncentivesByVin,
    getDealerOfferTotalNonMonthlyTaxesByVin,
    getDealerOfferTradeInAllowanceByVin,
    getDealerOfferTypeByVin,
    hasDealerOfferByVin,
} from "src/store/selectors/dealerOffer";
import { getTradeInEstimate } from "src/store/selectors/tradePending";
import {
    getDownPayment,
    getInterestRate,
    getLender,
    getOfferIncentives,
    getSelectedPaymentTerm,
    getSelectedPaymentType,
    getTotalFees,
    getTotalMonthlyTaxes,
    getTotalNonMonthlyTaxes,
    getVehicleMsrp,
    getVehiclePrice,
} from "src/store/selectors/vehicles";
import "./SidebarTable.scss";

interface Props {
    vin: string;
}

const SidebarTable = ({
    lender,
    bestBankRate,
    downPayment,
    price,
    msrp,
    showMsrp,
    showStrikethroughForMSRP,
    paymentType,
    term,
    accessoriesTotal,
    showAccessories,
    tradeInEstimate,
    incentivesTotal,
    fees,
    nonMonthlyTaxes,
    monthlyTaxes,
    accountDisclaimers,
}: Props & ReturnType<typeof mapStateToProps>) => {
    return (
        <ul className="SidebarTable">
            <Msrp msrp={msrp} price={price} showMsrp={showMsrp} showStrikethroughForMSRP={showStrikethroughForMSRP} />
            <Price accountDisclaimers={accountDisclaimers} msrp={msrp} price={price} showMsrp={showMsrp} />
            <PaymentType paymentType={paymentType} />
            <Term paymentType={paymentType} term={term} />
            <DownPayment accountDisclaimers={accountDisclaimers} downPayment={downPayment} paymentType={paymentType} />
            <BestBankRate paymentType={paymentType} rate={bestBankRate} lender={lender} />
            <TradeInEstimate tradeInEstimate={tradeInEstimate} />
            <Incentives incentivesTotal={incentivesTotal} />
            <Accessories accessoriesTotal={accessoriesTotal} showAccessories={showAccessories} />
            <Fees fees={fees} />
            <Taxes taxes={nonMonthlyTaxes} />
            <Taxes taxes={monthlyTaxes} monthly={true} />
        </ul>
    );
};

const mapStateToProps = (state: ApplicationStore, { vin }: Props) => {
    if (hasDealerOfferByVin(state, vin)) {
        return {
            accountDisclaimers: state.account.account_disclaimers,
            accessoriesTotal: getDealerOfferTotalAccessoriesByVin(state, vin),
            bestBankRate: getDealerOfferInterestRateByVin(state, vin),
            lender: getDealerOfferLenderByVin(state, vin),
            downPayment: getDealerOfferDownPaymentByVin(state, vin),
            fees: getDealerOfferTotalFeesByVin(state, vin),
            nonMonthlyTaxes: getDealerOfferTotalNonMonthlyTaxesByVin(state, vin),
            monthlyTaxes: 0,
            incentivesTotal: getDealerOfferTotalIncentivesByVin(state, vin),
            msrp: getVehicleMsrp(state, vin),
            price: getDealerOfferPriceByVin(state, vin),
            paymentType: getDealerOfferTypeByVin(state, vin) as string,
            showAccessories: true,
            showMsrp: canShowMsrp(state),
            showStrikethroughForMSRP: canShowStrikeThroughMsrp(state),
            term: getDealerOfferTermByVin(state, vin),
            tradeInEstimate: getDealerOfferTradeInAllowanceByVin(state, vin),
        };
    }

    return {
        accountDisclaimers: state.account.account_disclaimers,
        accessoriesTotal: getAccessoriesTotal(state, vin),
        bestBankRate: getInterestRate(state, vin),
        lender: getLender(state, vin),
        downPayment: getDownPayment(state, vin),
        fees: getTotalFees(state, vin),
        nonMonthlyTaxes: getTotalNonMonthlyTaxes(state, vin),
        monthlyTaxes: getTotalMonthlyTaxes(state, vin),
        incentivesTotal: getOfferIncentives(state, vin),
        msrp: getVehicleMsrp(state, vin),
        price: getVehiclePrice(state, vin),
        paymentType: getSelectedPaymentType(state, vin),
        showAccessories: isAccessoriesEnabled(state),
        showMsrp: canShowMsrp(state),
        showStrikethroughForMSRP: canShowStrikeThroughMsrp(state),
        term: getSelectedPaymentTerm(state, vin),
        tradeInEstimate: getTradeInEstimate(state),
    };
};

export default connect(mapStateToProps)(SidebarTable);

export const Row = ({ children }: { children: ReactNode }) => {
    return <li className="SidebarTable__row">{children}</li>;
};

export const LabelColumn = ({ children }: { children: ReactNode }) => {
    return <span className="SidebarTable__row-label">{children}</span>;
};

export const ValueColumn = ({ children }: { children: ReactNode }) => {
    return <span className="SidebarTable__row-value">{children}</span>;
};
